import { Injectable } from '@angular/core';
import {
  Transition,
  TransitionService,
  UIRouterGlobals,
} from '@uirouter/angular';
import { cloneDeep, defaults, isBoolean, merge } from 'lodash-es';

import {
  AprimaTransitionRequest,
} from 'webcommon/shared';

import { AprimaAnalyticsService } from '../AprimaAnalyticsService';
import { AppRouteService } from './AppRouteService';
import { WebViewManagerService } from './WebViewManagerService';

@Injectable({
  providedIn: 'root',
})
export class TransitionManagerService {
  static readonly ajsFactoryName = 'TransitionManager';

  constructor(
    private appRoutesService: AppRouteService,
    private aprimaAnalyticsService: AprimaAnalyticsService,
    private transitionService: TransitionService,
    private uiRouterGlobals: UIRouterGlobals,
    private webViewManagerService: WebViewManagerService,
  ) {
  }

  private previousTransition: AprimaTransitionRequest | undefined;
  private previousStateUrl: string | undefined;

  bootstrap() {
    this.transitionService.onStart({}, (transition) => this.onTransitionStart(transition));
    this.transitionService.onSuccess({}, () => this.onTransitionSuccess());
  }

  private onTransitionStart(transition: Transition): void {
    const fromState = transition.from() || {};
    const fromStateName = fromState && fromState.name || '';
    const toState = transition.targetState() || {};
    const toStateName = toState && toState.name() || '';

    const targetStateName = fromStateName || toStateName;
    const previousAppRoute = this.appRoutesService.findKeyByStateName(targetStateName);

    this.previousTransition = {
      appRoute: previousAppRoute || '',
      opts: cloneDeep(transition.params('from')),
    };
  }

  private onTransitionSuccess(): void {
    const currentState = this.uiRouterGlobals.current;
    const stateUrl = currentState.url || '/';

    if (stateUrl === this.previousStateUrl) {
      return;
    }

    this.previousStateUrl = stateUrl;
    // eslint-disable-next-line no-console
    console.info('url has changed: ' + stateUrl);
    this.aprimaAnalyticsService.sendPageView(stateUrl);
  }

  private formatTransition(transition: AprimaTransitionRequest): AprimaTransitionRequest | undefined {
    if (!transition) {
      console.warn('Cannot transition to null or undefined transition');
      return;
    }

    transition = defaults(transition, {
      type: 'transition',
      appRoute: '',
      opts: {},
    });

    // eslint-disable-next-line no-console
    console.info('TransitionManager postMessage to ' + JSON.stringify(transition));

    return transition;
  }

  transition(transition: AprimaTransitionRequest, replaceState?: boolean): boolean {
    const formattedTransition = this.formatTransition(transition);

    if (!formattedTransition) {
      return false;
    }

    transition = formattedTransition;

    const targetRoute = this.appRoutesService.getAppRoutes()[transition.appRoute];

    if (!targetRoute) {
      console.warn('Unable to find AppRoute: "' + transition.appRoute + '". Aborting transition.');
      return false;
    } else if (!transition.opts) {
      throw new Error('formatTransition failed');
    }

    if (!isBoolean(transition.opts.replaceCurrentState)) {
      if (isBoolean(replaceState)) {
        transition.opts.replaceCurrentState = replaceState;
      } else {
        const current = this.uiRouterGlobals.current;
        if (!current || current.name !== targetRoute.state) {
          transition.opts.replaceCurrentState = false;
        } else {
          transition.opts.replaceCurrentState = true;
        }
      }
    }

    if (targetRoute.options) {
      transition.opts = merge(transition.opts, targetRoute.options);
    }

    return this.webViewManagerService.activateView(targetRoute, transition);
  }

  transitionToLogin(): void {
    const appRoutes = this.appRoutesService.getAppRoutes();
    const current = this.uiRouterGlobals.current as any;

    // TODO: Change how this is detected to avoid typing to any
    if (current.alternativeLoginRoute){
      this.transition({ appRoute: appRoutes.statementslogin.state});
    } else {
      this.transition({ appRoute: appRoutes.login.state});
    }
  }

  replaceCurrentState(transition: AprimaTransitionRequest): void {
    this.transition(transition, true);
  }

  goBack(): void {
    this.webViewManagerService.goBack();
  }

  getPreviousTransition(): AprimaTransitionRequest {
    if (!this.previousTransition) {
      throw new Error('no previous transition');
    }

    return this.previousTransition;
  }
}
